import { compact, uniq } from 'lodash';
import React from 'react';
import { Button } from '../../../../../../components/Button/Button';
import { Form, useChanges } from '../../../../../../components/Form/Form';
import { Label } from '../../../../../../components/Form/Label/Label';
import { RadioGroup } from '../../../../../../components/Form/Radio/RadioGroup';
import { TextField } from '../../../../../../components/Form/TextField/TextField';
import { SubmitLine } from '../../../../../../components/SubmitLine/SubmitLine';
import { t } from '../../../../../../locales/translate';
import { getEventSalesStatus } from '../../../../../../model/event';
import { RefundModel, RefundOption } from '../../../../../../model/order';
import { returnTickets } from '../../../../../../model/ticket';
import { VenuePlan } from '../../../../../../model/venue';
import { present } from '../../../../../../util/util';
import { PrintedTicket } from './order_tickets';
import { notNil } from '../../../../../../util/values';

interface Props {
  event: {
    id: string;
    venuePlan?: VenuePlan;
  };
  tickets: PrintedTicket[];
  onSuccess: (info: { generalSectionIds: Array<string>; seated: boolean }) => void;
  onClose: () => void;
}

export function TicketReturn({ onSuccess, onClose, tickets, event }: Props): JSX.Element | null {
  const options: Array<{ label: string; value: RefundOption }> = [
    {
      label: t.model.ticket.cancelAndRelease,
      value: 'refund-release',
    },
    {
      label: t.model.ticket.cancelAndBlock,
      value: 'refund-block',
    },
  ];
  const [, , model, trackChanges] = useChanges<RefundModel>({
    option: options[0].value,
  });

  if (!tickets) {
    return null;
  }

  const filteredTickets = tickets.filter(notNil);

  async function submit(data: RefundModel): Promise<void> {
    await returnTickets({
      itemIds: filteredTickets.map((e) => e?.id),
      blockReason: data.option === 'refund-block' ? data.blockReason : undefined,
    });
    getEventSalesStatus(event.id, { force: true });
    onSuccess({
      seated: filteredTickets.some((t) => t.seat),
      generalSectionIds: uniq(compact(filteredTickets.map((t) => t.venueSectionId))).filter((s) => {
        return event.venuePlan?.sections?.some((planSection) => planSection.generalAdmission && planSection.id === s);
      }),
    });
    onClose();
  }

  const canSubmit = model.option === 'refund-release' || present(model.blockReason);

  return (
    <div className="refund-modal">
      <Label title={t.model.ticket.refundOptions}></Label>
      <Form model={model} onInput={trackChanges} onSubmit={submit}>
        <RadioGroup name="option" values={options}></RadioGroup>
        {model.option === 'refund-block' && (
          <TextField required name="blockReason" label={t.model.contingent.blockReason} />
        )}
        <SubmitLine>
          <Button title={t.actions.cancel('')} onClick={onClose} />
          <Button title={t.actions.save('')} type="primary" disabled={!canSubmit} isSubmit />
        </SubmitLine>
      </Form>
    </div>
  );
}
